// src/components/global/Table/TableFilterBar.jsx
import React, { useMemo } from 'react';
import {
    Box,
    Stack,
    FormControl,
    Select,
    MenuItem,
    Button,
    Chip,
    Typography,
} from '@mui/material';
import { FilterList, Clear } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

/**
 * TableFilterBar - Generic filter component for tables
 * 
 * @param {Array} filters - Array of filter configurations
 * @param {Function} onClearAll - Callback to clear all filters
 * 
 * Filter config structure:
 * {
 *   type: 'select',
 *   name: 'status',
 *   label: { ar: 'الحالة', en: 'Status' },
 *   options: [
 *     { value: '', label: { ar: 'الكل', en: 'All' } },
 *     { value: 'active', label: { ar: 'نشط', en: 'Active' } }
 *   ],
 *   value: statusFilter,
 *   onChange: (value) => setStatusFilter(value)
 * }
 */
const TableFilterBar = ({ filters = [], onClearAll }) => {
    const theme = useTheme();
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const isDark = theme.palette.mode === 'dark';

    // Count active filters (filters with non-empty values)
    const activeFilterCount = useMemo(() => {
        return filters.filter((f) => f.value && f.value !== '').length;
    }, [filters]);

    // Don't render if no filters provided
    if (!filters || filters.length === 0) {
        return null;
    }

    const handleClearAll = () => {
        filters.forEach((filter) => {
            if (filter.onChange) {
                filter.onChange('');
            }
        });
        if (onClearAll) onClearAll();
    };

    return (
        <Box
            sx={{
                px: 2.5,
                py: 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
                bgcolor: isDark ? 'rgba(15,23,42,0.5)' : 'rgba(249,250,251,0.8)',
            }}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
            >
                {/* Left side: Filters */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    sx={{ flex: 1 }}
                >
                    {/* Filter icon + label */}
                    <Stack direction="row" spacing={1} alignItems="center">
                        <FilterList
                            fontSize="small"
                            sx={{ color: theme.palette.text.secondary }}
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                color: theme.palette.text.secondary,
                            }}
                        >
                            {isArabic ? 'تصفية حسب:' : 'Filter by:'}
                        </Typography>
                    </Stack>

                    {/* Render filters */}
                    {filters.map((filter) => (
                        <FormControl
                            key={filter.key || filter.name}
                            size="small"
                            sx={{
                                minWidth: 160,
                                width: { xs: '100%', sm: 'auto' },
                            }}
                        >
                            <Select
                                value={filter.value || ''}
                                onChange={(e) => filter.onChange(e.target.value)}
                                displayEmpty
                                sx={{
                                    bgcolor: isDark ? 'rgba(30,41,59,0.8)' : '#FFFFFF',
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: isDark
                                            ? 'rgba(148,163,184,0.3)'
                                            : 'rgba(0,0,0,0.12)',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.primary.main,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.primary.main,
                                    },
                                }}
                            >
                                {filter.options?.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        <Typography variant="body2">
                                            {typeof option.label === 'object' 
                                                ? (isArabic ? option.label.ar : option.label.en)
                                                : option.label}
                                        </Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ))}
                </Stack>

                {/* Right side: Clear button + active count */}
                <Stack direction="row" spacing={1} alignItems="center">
                    {activeFilterCount > 0 && (
                        <Chip
                            label={`${activeFilterCount} ${isArabic ? 'فلتر نشط' : 'active'}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                        />
                    )}

                    {activeFilterCount > 0 && (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Clear fontSize="small" />}
                            onClick={handleClearAll}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                borderColor: isDark
                                    ? 'rgba(148,163,184,0.3)'
                                    : 'rgba(0,0,0,0.12)',
                                color: theme.palette.text.secondary,
                                '&:hover': {
                                    borderColor: theme.palette.error.main,
                                    color: theme.palette.error.main,
                                    bgcolor: isDark
                                        ? 'rgba(239,68,68,0.08)'
                                        : 'rgba(239,68,68,0.04)',
                                },
                            }}
                        >
                            {isArabic ? 'مسح الفلاتر' : 'Clear filters'}
                        </Button>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
};

export default TableFilterBar;